"use client";

import { useSession } from "next-auth/react";
import { usePathname } from "next/navigation";
import { useReportWebVitals } from "next/web-vitals";
import { useEffect, useMemo, useRef, useState } from "react";
import { env } from "~/env.mjs";
import { useCurrentUrlWithoutHash } from "~/hooks/use-current-url-without-hash";
import {
  COOKIE_CONSENT_STORAGE_KEY,
  getBrowserCookieValue,
  getBrowserConsentCountryCode,
  getConsentDefault,
  parseStoredConsent,
  consentStateFromDefault,
  type ConsentState,
} from "~/lib/consent-region";
import { posthog, isPostHogBrowserEnabled } from "~/lib/posthog/client";
import {
  getPostHogPageCategory,
  isPostHogReplayBlockedPath,
  shouldAutoDisplayPostHogSurvey,
} from "~/lib/posthog/config";

function getStoredConsent(initialConsent: ConsentState): ConsentState {
  if (typeof window === "undefined") {
    return initialConsent;
  }

  return (
    parseStoredConsent(localStorage.getItem(COOKIE_CONSENT_STORAGE_KEY)) ??
    parseStoredConsent(getBrowserCookieValue(COOKIE_CONSENT_STORAGE_KEY)) ??
    consentStateFromDefault(
      getConsentDefault(getBrowserConsentCountryCode()),
    ) ??
    initialConsent
  );
}

const WEB_VITAL_SAMPLE_RATE = 0.1;
const REPLAY_SAMPLE_RATE = 0.05;

export function PostHogAppProvider({
  initialConsent,
}: {
  initialConsent: ConsentState;
}) {
  const { data: session, status } = useSession();
  const pathname = usePathname();
  const currentUrl = useCurrentUrlWithoutHash();
  const [storageKey, setStorageKey] = useState(0);
  const consent = useMemo(
    () => getStoredConsent(initialConsent),
    [initialConsent, storageKey],
  );
  const previousIdentifiedUserIdRef = useRef<string | null>(null);
  const replaySampledRef = useRef<boolean | null>(null);
  const authContextRef = useRef<{
    isAuthenticated: boolean;
    userRole?: string;
  }>({ isAuthenticated: false });

  authContextRef.current = {
    isAuthenticated: status === "authenticated",
    userRole: session?.user?.role,
  };

  useReportWebVitals((metric) => {
    if (!isPostHogBrowserEnabled()) {
      return;
    }

    if (Math.random() > WEB_VITAL_SAMPLE_RATE) {
      return;
    }

    posthog.capture("web_vital", {
      metric_id: metric.id,
      metric_name: metric.name,
      metric_label: metric.label,
      metric_value: metric.value,
      page_path: pathname,
      page_location: currentUrl,
      page_category: pathname ? getPostHogPageCategory(pathname) : undefined,
      is_authenticated: authContextRef.current.isAuthenticated,
      user_role: authContextRef.current.userRole,
      environment: env.NEXT_PUBLIC_VERCEL_ENV,
      sample_rate: WEB_VITAL_SAMPLE_RATE,
    });
  });

  useEffect(() => {
    const handleStorageChange = () => {
      setStorageKey((previous) => previous + 1);
    };
    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, []);

  // Cookieless anonymous capture until analytics consent. Consent upgrades
  // persistence so identity survives across visits; withdrawal clears stored
  // identifiers before dropping back to memory-only.
  useEffect(() => {
    if (!isPostHogBrowserEnabled()) {
      return;
    }

    // Opt-in/out flags persisted by the previous opt-out-by-default setup
    // would silently block all capture, even after consent is granted.
    posthog.clear_opt_in_out_capturing();

    if (consent.analyticsConsent) {
      posthog.set_config({ persistence: "localStorage+cookie" });
    } else {
      // Switch persistence first: the migration clears the on-disk store,
      // so the subsequent reset only ever rotates the in-memory identity
      // and never writes a fresh id to localStorage/cookie.
      posthog.set_config({ persistence: "memory" });
      posthog.reset();
      previousIdentifiedUserIdRef.current = null;
    }
  }, [consent.analyticsConsent]);

  // User identification, only once analytics consent links activity to the
  // account. Without consent, authenticated users stay anonymous.
  useEffect(() => {
    if (!isPostHogBrowserEnabled()) {
      return;
    }

    const currentUserId = status === "authenticated" ? session?.user?.id : null;
    const previousUserId = previousIdentifiedUserIdRef.current;

    if (
      status === "authenticated" &&
      currentUserId &&
      consent.analyticsConsent
    ) {
      posthog.identify(currentUserId, {
        role: session.user.role,
      });
      previousIdentifiedUserIdRef.current = currentUserId;
      return;
    }

    if (status === "unauthenticated" && previousUserId) {
      posthog.reset();
      previousIdentifiedUserIdRef.current = null;
    }
  }, [session, status, consent.analyticsConsent]);

  // Custom pageview with page category
  useEffect(() => {
    if (!pathname || !currentUrl || !isPostHogBrowserEnabled()) {
      return;
    }

    posthog.capture("$pageview", {
      $current_url: currentUrl,
      page_category: getPostHogPageCategory(pathname),
      is_authenticated: authContextRef.current.isAuthenticated,
      user_role: authContextRef.current.userRole,
    });
  }, [currentUrl, pathname]);

  // Path-based replay and survey control. Session recording additionally
  // requires analytics consent; surveys follow path rules for everyone.
  useEffect(() => {
    if (!pathname || !isPostHogBrowserEnabled()) {
      return;
    }

    const replayAllowed =
      consent.analyticsConsent &&
      !isPostHogReplayBlockedPath(pathname) &&
      (replaySampledRef.current ??= Math.random() < REPLAY_SAMPLE_RATE);

    posthog.set_config({
      disable_surveys_automatic_display:
        !shouldAutoDisplayPostHogSurvey(pathname),
      disable_session_recording: !replayAllowed,
    });
  }, [consent.analyticsConsent, pathname]);

  return null;
}
